import React, { useState, useEffect, useCallback } from "react";
import {
  GoogleMap,
  useLoadScript,
  InfoWindow,
  Marker,
} from "@react-google-maps/api";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import axios from "axios";
import { useLocation, Link } from "react-router-dom";
import queryString from "query-string";
import "./DemoLocationFinder.css";
import Spinner from "./Spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faList,
  faArrowDown,
  faArrowUp,
  faTimes,
  faMapMarkerAlt,
  faBars,
} from "@fortawesome/free-solid-svg-icons";
import LeadGenForm from "./LeadGenForm";
import ReactPixel from "react-facebook-pixel";

const googleMapsLibraries = ["places"];

const LocationFinderIntegrityDemo = () => {
  const [address, setAddress] = useState("");
  const [coordinates, setCoordinates] = useState({});
  const [radius, setRadius] = useState("");
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [sortCriteria, setSortCriteria] = useState({
    field: "price",
    ascending: true,
  });
  const [userLocation, setUserLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState({
    lat: 39.8283,
    lng: -98.5795,
  });
  const [mapZoom, setMapZoom] = useState(4);
  const [mapInstance, setMapInstance] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(true);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isLeadSubmitted, setIsLeadSubmitted] = useState(() =>
    localStorage.getItem("leadSubmitted") === "true"
  );
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);

  const location = useLocation();
  const apiUrl = process.env.REACT_APP_API_URL;
  const ipInfoToken = "ad22ffe4eb7ee0";
  const theme = {
    primary: "#00b1da", // Integrity's cyan for buttons and accents
  };

  const hardcodedUserId = "okb9nhuDaWZFt137rWWKskh9IaJ3";

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: googleMapsLibraries,
  });

  useEffect(() => {
    const params = queryString.parse(window.location.search);
    if (Object.keys(params).length > 0) {
      localStorage.setItem("marketingParams", JSON.stringify(params));
    }
  }, []);

  useEffect(() => {
    const pageName = location.pathname.replace("/", "") || "Home";
    ReactPixel.pageView({
      page: pageName,
      path: location.pathname,
      url: window.location.href,
    });
  }, [location]);

  useEffect(() => {
    const fetchUserLocation = async () => {
      try {
        const response = await axios.get(
          `https://ipinfo.io/json?token=${ipInfoToken}`
        );
        const [lat, lng] = response.data.loc.split(",");
        setUserLocation({ lat: parseFloat(lat), lng: parseFloat(lng) });
      } catch (error) {
        console.error("Error getting user location from IPInfo:", error);
      }
    };
    fetchUserLocation();
  }, []);

  useEffect(() => {
    setCoordinates({});
  }, [address]);

  useEffect(() => {
    const isValid = address.trim() !== "" && radius > 0 && radius <= 3500;
    setIsFormValid(isValid);
  }, [address, radius]);

  const handleSelect = async (value) => {
    const results = await geocodeByAddress(value);
    const latLng = await getLatLng(results[0]);
    setAddress(value);
    setCoordinates(latLng);
  };

  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const toRad = (x) => (x * Math.PI) / 180;
    const R = 3958.8; // miles
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const fetchFuelStops = async (force = false) => {
    if (radius > 3500) {
      alert("Radius should be 3500 or below.");
      return;
    }
    if (!isAuthorized) {
      alert("Unauthorized access.");
      return;
    }

    if (!force && !isLeadSubmitted) {
      setIsLeadModalOpen(true);
      return;
    }
    setLoading(true);

    let currentCoordinates = coordinates;
    const isValidCoordinates =
      coordinates &&
      typeof coordinates.lat === "number" &&
      typeof coordinates.lng === "number";

    if (!isValidCoordinates && address) {
      try {
        const results = await geocodeByAddress(address);
        const latLng = await getLatLng(results[0]);
        setCoordinates(latLng);
        currentCoordinates = latLng;
        setMapCenter(latLng);
        setMapZoom(10);
      } catch (error) {
        console.error("Error geocoding address:", error);
        setLoading(false);
        return;
      }
    }

    const data = {
      latitude: currentCoordinates.lat,
      longitude: currentCoordinates.lng,
      searchRange: radius,
      UserId: hardcodedUserId,
    };

    try {
      const response = await fetch(`${apiUrl}/getLocationsV2`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        console.error(
          "Fetch failed with status:",
          response.status,
          response.statusText
        );
        setLoading(false);
        return;
      }

      const locationsData = await response.json();
      if (userLocation) {
        locationsData.forEach((location) => {
          location.distance = calculateDistance(
            userLocation.lat,
            userLocation.lng,
            parseFloat(location.latitude),
            parseFloat(location.longitude)
          ).toFixed(2);
        });
      }
      setLocations(locationsData);
      setIsPanelOpen(true);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching fuel stops:", error);
      setLoading(false);
    }
  };

  const togglePanel = () => {
    setIsPanelOpen(!isPanelOpen);
  };

  useEffect(() => {
    if (mapInstance) {
      window.google.maps.event.trigger(mapInstance, "resize");
      mapInstance.setCenter(mapCenter);
    }
  }, [isPanelOpen, mapInstance, mapCenter]);

  const handleSort = (field) => {
    const isAscending =
      sortCriteria.field === field ? !sortCriteria.ascending : true;
    setSortCriteria({ field, ascending: isAscending });
  };

  const sortedLocations = [...locations].sort((a, b) => {
    let aValue, bValue;
    if (sortCriteria.field === "price") {
      aValue = parseFloat(a.userPrice) || Infinity;
      bValue = parseFloat(b.userPrice) || Infinity;
    } else if (sortCriteria.field === "distance") {
      aValue = parseFloat(a.distance) || Infinity;
      bValue = parseFloat(b.distance) || Infinity;
    }
    return sortCriteria.ascending ? aValue - bValue : bValue - aValue;
  });

  const handleMapLoad = useCallback((map) => {
    setMapInstance(map);
  }, []);

  const renderMarkers = () => {
    return sortedLocations.map((location) => {
      if (!location.retailPrice || location.retailPrice === "NA") return null;
      const markerIcon = {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
            <path fill="#00b1da" d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z"/>
          </svg>
        `)}`,
        labelOrigin: new window.google.maps.Point(12, 45),
        scaledSize: new window.google.maps.Size(30, 30),
      };

      return (
        <Marker
          key={`marker-${location.locationID}`}
          position={{
            lat: parseFloat(location.latitude),
            lng: parseFloat(location.longitude),
          }}
          icon={markerIcon}
          onClick={() => setSelectedLocation(location)}
          label={{
            text:
              location.userPrice === "TBD"
                ? `$${parseFloat(location.retailPrice).toFixed(3)}`
                : `$${parseFloat(location.userPrice).toFixed(3)}`,
            color: "#ffffff",
            fontSize: "14px",
            fontWeight: "bold",
            className: "price-label",
          }}
        />
      );
    });
  };

  const renderInfoWindow = () => {
    if (!selectedLocation) return null;
    const savings = (
      parseFloat(selectedLocation.retailPrice) -
      parseFloat(selectedLocation.userPrice)
    ).toFixed(2);
    const price = isNaN(parseFloat(selectedLocation.userPrice))
      ? "$0.00"
      : `$${parseFloat(selectedLocation.userPrice).toFixed(3)}`;
    const retailPrice = isNaN(parseFloat(selectedLocation.retailPrice))
      ? "N/A"
      : `$${parseFloat(selectedLocation.retailPrice).toFixed(3)}`;
    const discount = isNaN(savings) ? "Pricing Unavailable" : `${savings} Off`;
    const distance = selectedLocation.distance ? `${selectedLocation.distance}` : "";
    const modifiedOn = new Date(selectedLocation.modifiedOn).toLocaleString();

    return (
      <InfoWindow
        position={{
          lat: parseFloat(selectedLocation.latitude),
          lng: parseFloat(selectedLocation.longitude),
        }}
        onCloseClick={() => setSelectedLocation(null)}
        options={{ pixelOffset: new window.google.maps.Size(0, -30) }}
      >
        <div className="info-window-content">
          <p className="price-note">
            *Prices may vary and are subject to change. We strive to provide
            accurate information, but discrepancies may occur.
          </p>
          <div className="info-window-details">
            <h2>{selectedLocation.locationName}</h2>
            <p className="directions-link">
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                  selectedLocation.addr2 +
                    ", " +
                    selectedLocation.city +
                    ", " +
                    selectedLocation.state +
                    " " +
                    selectedLocation.zip
                )}${
                  userLocation
                    ? `&origin=${userLocation.lat},${userLocation.lng}`
                    : ""
                }`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Directions
              </a>
            </p>
            <div className="price-info">
              <div className="user-price">{price}</div>
              <div className="price-label">Your Price</div>
              <div className="retail-price">{retailPrice}</div>
              <div className="price-label">Retail Price</div>
              <div className="discount">{discount}</div>
            </div>
            <p className="address">
              {selectedLocation.addr1}, {selectedLocation.city},{" "}
              {selectedLocation.state} {selectedLocation.zip}
            </p>
            {distance && (
              <p className="distance">
                <FontAwesomeIcon
                  icon={faMapMarkerAlt}
                  className="mr-1"
                  style={{ color: theme.primary }}
                />
                {distance} miles away
              </p>
            )}
            <p className="last-update">Last Price Update: {modifiedOn}</p>
          </div>
        </div>
      </InfoWindow>
    );
  };

  const handleLocationClick = (location) => {
    setSelectedLocation(location);
    const position = {
      lat: parseFloat(location.latitude),
      lng: parseFloat(location.longitude),
    };
    setMapCenter(position);
    setMapZoom(14);
    if (mapInstance) {
      mapInstance.panTo(position);
      mapInstance.setZoom(14);
    }
  };

  if (loadError) {
    return <div>Error loading maps</div>;
  }

  if (!isLoaded) {
    return <Spinner />;
  }

  return (
    <>
      {!isAuthorized ? (
        <div className="unauthorized">
          <h1>Unauthorized Access</h1>
          <p>You do not have permission to access this page.</p>
        </div>
      ) : (
        <div className="location-finder" style={{ backgroundColor: "#ffffff" }}>
          {/* Header */}
          <header className="top-0 z-50 bg-white shadow-md mb-4">
            <div className="container mx-auto px-6 py-2 flex items-center justify-between">
              <div className="py-2 flex items-center space-x-3">
                <img
                  src="/logos/integrity_logo_big.png"
                  alt="Integrity Factoring Logo"
                  className="h-20 w-auto object-contain"
                />
              </div>
              <nav className="hidden md:flex space-x-6">
                <Link
                  to="/"
                  className="text-[#1e365c] hover:text-[#00b1da] transition"
                >
                  Home
                </Link>
                <Link
                  to="/#contact"
                  className="text-[#1e365c] hover:text-[#00b1da] transition"
                >
                  Contact
                </Link>
                <Link
                  to="/registration"
                  className="text-[#1e365c] hover:text-[#00b1da] transition"
                >
                  Sign Up
                </Link>
              </nav>
              <div className="md:hidden">
                <button
                  onClick={() => setIsNavOpen(!isNavOpen)}
                  className="text-[#1e365c] focus:outline-none"
                >
                  <FontAwesomeIcon
                    icon={faBars}
                    className="text-[#00b1da] text-4xl"
                  />
                </button>
              </div>
            </div>
            <nav
              className={`
                md:hidden bg-white transition-all duration-300
                ${isNavOpen ? "block" : "hidden"}
              `}
            >
              <div className="px-6 py-2">
                <Link
                  to="/"
                  className="block text-[#1e365c] hover:bg-[#00b1da] hover:text-white rounded py-2"
                  onClick={() => setIsNavOpen(false)}
                >
                  Home
                </Link>
                <Link
                  to="/#contact"
                  className="block text-[#1e365c] hover:bg-[#00b1da] hover:text-white rounded py-2"
                  onClick={() => setIsNavOpen(false)}
                >
                  Contact
                </Link>
                <Link
                  to="/registration"
                  className="block text-[#1e365c] hover:bg-[#00b1da] hover:text-white rounded py-2"
                  onClick={() => setIsNavOpen(false)}
                >
                  Sign Up
                </Link>
              </div>
            </nav>
          </header>

          {/* Info Section */}
          <section
            className="info-section bg-gradient-to-b from-white to-[#e6f0fa]"
            style={{ textAlign: "center", padding: "20px" }}
          >
            <h1
              className="text-[#ffb636]"
              style={{
                fontSize: "1.5rem",
                fontWeight: "bold",
                marginBottom: "1rem",
              }}
            >
              Unlock Fuel Savings with Integrity Factoring
            </h1>
            <p style={{ marginBottom: "1rem", color: "#1e365c" }}>
              Explore average discounts at over 4,000+ fueling locations
              nationwide. With an Integrity fuel card, you’ll enjoy big savings
              on every gallon.
            </p>
            <p style={{ marginBottom: "1rem", color: "#1e365c" }}>
              Our platform offers powerful tools like real-time fuel pricing,
              card management, and instant fund transfers via our Tank Payments
              partnership.
            </p>
            <p>
              <Link
                to="/registration"
                style={{ color: "#00b1da", textDecoration: "underline" }}
              >
                Sign up today
              </Link>{" "}
              — it’s free, fast, and hassle-free!
            </p>
          </section>

          {/* Main Content */}
          <div className="main-content" style={{ backgroundColor: "#e6f0fa" }}>
            {/* Search Form */}
            <div className="search-form bg-white shadow-md p-4 rounded-lg">
              <div className="search-form-inner flex flex-wrap gap-4 justify-center">
                <div className="autocomplete flex-1 min-w-[200px]">
                  <PlacesAutocomplete
                    value={address}
                    onChange={setAddress}
                    onSelect={handleSelect}
                  >
                    {({
                      getInputProps,
                      suggestions,
                      getSuggestionItemProps,
                      loading: placesLoading,
                    }) => (
                      <div>
                        <input
                          {...getInputProps({
                            placeholder: "Search City/State",
                          })}
                          className="input w-full p-2 border border-[#1e365c] rounded"
                          required
                        />
                        <div className="autocomplete-dropdown">
                          {placesLoading && <div>Loading...</div>}
                          {suggestions.map((suggestion) => (
                            <div
                              {...getSuggestionItemProps(suggestion)}
                              key={suggestion.placeId}
                              className={`suggestion ${
                                suggestion.active ? "active" : ""
                              }`}
                            >
                              {suggestion.description}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </PlacesAutocomplete>
                </div>
                <input
                  type="number"
                  max="3500"
                  value={radius}
                  onChange={(e) => {
                    if (e.target.value <= 3500) {
                      setRadius(e.target.value);
                    }
                  }}
                  placeholder="Radius (miles)"
                  className="input radius-input p-2 border border-[#1e365c] rounded w-32"
                  required
                />
                <button
                  onClick={() => fetchFuelStops()}
                  className="themed-button2 bg-[#00b1da] hover:bg-[#ffb636] text-white p-2 rounded disabled:opacity-50"
                  disabled={!isFormValid}
                >
                  Search
                </button>
                <button
                  onClick={togglePanel}
                  className="list-toggle-button bg-[#1e365c] hover:bg-[#00b1da] text-white p-2 rounded flex items-center"
                >
                  <FontAwesomeIcon icon={faList} className="mr-2" />
                  {isPanelOpen ? "Hide List" : "Show List"}
                </button>
              </div>
            </div>

            {/* Loading Spinner */}
            {loading && <Spinner />}

            {/* Map and List */}
            <div className="content flex flex-col lg:flex-row gap-4 p-4">
              {/* Map */}
              <div
                className={`map-container flex-1 ${
                  isPanelOpen ? "lg:w-2/3" : "w-full"
                }`}
                style={{ height: "100%" }}
              >
                <GoogleMap
                  onLoad={handleMapLoad}
                  center={mapCenter}
                  zoom={mapZoom}
                  mapContainerStyle={{ width: "100%", height: "100%" }}
                >
                  {renderMarkers()}
                  {renderInfoWindow()}
                </GoogleMap>
              </div>

              {/* List Panel */}
              {isPanelOpen && (
                <div className="list-panel w-full lg:w-1/3 bg-white shadow-md rounded-lg p-4 max-h-[100%] overflow-y-auto">
                  <div className="list-header flex justify-between items-center mb-4">
                    <div className="sort-options flex gap-2">
                      <span className="text-[#1e365c]">Sort By:</span>
                      <button
                        onClick={() => handleSort("price")}
                        className="sort-button text-[#00b1da] hover:text-[#ffb636]"
                      >
                        <FontAwesomeIcon
                          icon={
                            sortCriteria.field === "price" &&
                            sortCriteria.ascending
                              ? faArrowUp
                              : faArrowDown
                          }
                          className="mr-1"
                        />
                        Best Price
                      </button>
                      <button
                        onClick={() => handleSort("distance")}
                        className="sort-button text-[#00b1da] hover:text-[#ffb636]"
                      >
                        <FontAwesomeIcon
                          icon={
                            sortCriteria.field === "distance" &&
                            sortCriteria.ascending
                              ? faArrowUp
                              : faArrowDown
                          }
                          className="mr-1"
                        />
                        Closest
                      </button>
                    </div>
                    <button
                      onClick={togglePanel}
                      className="btn btn-close text-[#1e365c] hover:text-[#00b1da]"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                  <div className="price-note text-gray-600 text-sm mb-4">
                    <p>
                      *Prices may vary and are subject to change. We strive to
                      provide accurate information, but discrepancies may occur.
                    </p>
                  </div>
                  <div className="locations-list">
                    {sortedLocations.map((location) => {
                      const savings = (
                        parseFloat(location.retailPrice) -
                        parseFloat(location.userPrice)
                      ).toFixed(2);
                      const price = isNaN(parseFloat(location.userPrice))
                        ? "$0.00"
                        : `$${parseFloat(location.userPrice).toFixed(3)}`;
                      const retailPrice = isNaN(
                        parseFloat(location.retailPrice)
                      )
                        ? "N/A"
                        : `$${parseFloat(location.retailPrice).toFixed(3)}`;
                      const discount = isNaN(savings)
                        ? "Pricing Unavailable"
                        : `$${savings} Off`;
                      const distance = location.distance
                        ? `${location.distance}`
                        : "";
                      const modifiedOn = new Date(
                        location.modifiedOn
                      ).toLocaleString();

                      return (
                        <div
                          key={location.locationID}
                          className="location-item p-4 border-b border-gray-200 hover:bg-[#e6f0fa] cursor-pointer"
                          onClick={() => handleLocationClick(location)}
                        >
                          <div className="location-item-inner flex justify-between">
                            <div className="location-info">
                              <div className="location-name font-bold text-[#1e365c]">
                                {location.locationName}
                              </div>
                              <div className="directions-link">
                                <a
                                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                                    location.addr2 +
                                      ", " +
                                      location.city +
                                      ", " +
                                      location.state +
                                      " " +
                                      location.zip
                                  )}${
                                    userLocation
                                      ? `&origin=${userLocation.lat},${userLocation.lng}`
                                      : ""
                                  }`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#00b1da] hover:underline"
                                >
                                  Directions
                                </a>
                              </div>
                              <div className="address text-gray-700">
                                {location.addr1}, {location.city},{" "}
                                {location.state} {location.zip}
                              </div>
                              {distance && (
                                <div className="distance text-gray-700">
                                  <FontAwesomeIcon
                                    icon={faMapMarkerAlt}
                                    className="mr-1"
                                    style={{ color: theme.primary }}
                                  />
                                  {distance} miles away
                                </div>
                              )}
                            </div>
                            <div className="price-info text-right">
                              <div className="user-price text-[#00b1da] font-bold">
                                {price}
                              </div>
                              <div className="price-label text-gray-600 text-sm">
                                Your Price
                              </div>
                              <div className="discount text-[#ffb636] font-semibold">
                                {discount}
                              </div>
                              <div className="retail-price text-gray-700 line-through">
                                {retailPrice}
                              </div>
                              <div className="price-label text-gray-600 text-sm">
                                Retail Price
                              </div>
                            </div>
                          </div>
                          <div className="last-update text-gray-600 text-sm mt-2">
                            Last Price Update: {modifiedOn}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <footer className="footer1 bg-[#1e365c] text-white py-6">
            <div className="container mx-auto px-6 text-center">
              <p>
                © {new Date().getFullYear()} Integrity Factoring. All Rights
                Reserved.
              </p>
            </div>
          </footer>

          {/* Lead Gen Modal */}
          {isLeadModalOpen && (
            <div className="lead-modal-overlay fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-2">
              <LeadGenForm
                onSuccess={() => {
                  localStorage.setItem("leadSubmitted", "true");
                  setIsLeadSubmitted(true);
                  setIsLeadModalOpen(false);
                  fetchFuelStops(true);
                }}
                onClose={() => setIsLeadModalOpen(false)}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default LocationFinderIntegrityDemo;
